import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { listProducts } from "../../Redux/Actions/ProductActions";
import Message from "../LoadingError/Error";
import CategoryTabs from "./CategoryTabs";
import Pagination, { PaginationSkeleton } from "./pagination";
import Rating from "./Rating";
import SkeletonGrid from "./skeletoncard";
import { Helmet } from "react-helmet-async";
import "./shop.css";

const cloudinaryOptimize = (url, width = 300) => {
  if (!url || !url.includes("cloudinary.com")) return url;
  return url.replace("/upload/", `/upload/w_${width},f_auto,q_auto/`);
};

const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    let timer;
    const handleResize = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth <= 768), 150);
    };
    window.addEventListener("resize", handleResize);
    return () => { clearTimeout(timer); window.removeEventListener("resize", handleResize); };
  }, []);
  return isMobile;
};

const ProductCard = React.memo(({ product, isMobile, isHovered, onMouseEnter, onMouseLeave, isFirstProduct }) => {
  const inStock = product.countInStock > 0;
  const price = Number(product.price);
  const optimizedSrc = cloudinaryOptimize(product.images?.[0] || product.image || "/default-image.jpg", 300);

  return (
    <div className="shop shop-4col col-md-4 col-4" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div className="border-product" style={{ position: "relative", overflow: "visible" }}>
        <Link to={`/products/${product._id}`}>
          <div className="shopBack" style={{ overflow: "hidden" }}>
            <img
              src={optimizedSrc} alt={product.name}
              loading={isFirstProduct ? "eager" : "lazy"}
              fetchpriority={isFirstProduct ? "high" : "auto"}
              decoding={isFirstProduct ? "sync" : "async"}
              className={`product-image ${isHovered ? "image-hover" : ""}`}
            />
          </div>
        </Link>
        <div className="shoptext">
          {isMobile ? (
            <>
              <div className={`stock-status ${inStock ? "in-stock" : "out-of-stock"}`}>{inStock ? "In Stock" : "Out of Stock"}</div>
              <div className="price-container text-center"><span className="price-regular">${price.toFixed(2)}</span></div>
            </>
          ) : (
            <>
              <p className="product-name"><Link to={`/products/${product._id}`}>{product.name}</Link></p>
              <div className={`stock-status ${inStock ? "in-stock" : "out-of-stock"}`}>{inStock ? "In Stock • Ready to Ship" : "Out of Stock"}</div>
              <Rating value={product.rating} text={`${product.numReviews} reviews`} />
              <div className="price-container text-center"><span className="price-regular">${price.toFixed(2)}</span></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
});

const ShopSection = () => {
  const dispatch = useDispatch();
  const { keyword = "", pageNumber = "1" } = useParams();
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const isMobile = useIsMobile();

  const productList = useSelector((state) => state.productList);
  const { loading, error, products, page, pages } = productList;

  useEffect(() => { dispatch(listProducts(keyword, pageNumber)); }, [dispatch, keyword, pageNumber]);

  const productList_ = useMemo(() => (!products ? [] : [...products]), [products]);
  const handleMouseEnter = useCallback((id) => () => setHoveredProduct(id), []);
  const handleMouseLeave = useCallback(() => setHoveredProduct(null), []);

  const firstProductImage = productList_[0]
    ? cloudinaryOptimize(productList_[0].images?.[0] || productList_[0].image, 300)
    : null;

  return (
    <>
      <Helmet>
        <title>{keyword ? `"${keyword}" - Search Results | Silent Exchange` : "Shop Collectibles | Silent Exchange"}</title>
        <meta name="description" content={keyword ? `Search results for "${keyword}" - Browse Pokemon cards, sports cards, and collectibles at Silent Exchange.` : "Browse our full collection of Pokemon cards, sports cards, vintage toys and collectibles at Silent Exchange."} />
        <link rel="canonical" href={keyword ? `https://silentexchange.store/search/${keyword}` : "https://silentexchange.store"} />
        {firstProductImage && <link rel="preload" as="image" href={firstProductImage} fetchpriority="high" />}
      </Helmet>

      <div className="container">
        <div className="section">
          <CategoryTabs />
          <div className="row">
            <div className="col-lg-12 col-md-12 article">
              <div className="shopcontainer row">
                {loading ? (
                  <SkeletonGrid count={9} isMobile={isMobile} />
                ) : error ? (
                  <Message variant="alert-danger">{error}</Message>
                ) : (
                  productList_.map((product, index) => (
                    <ProductCard
                      key={product._id} product={product} isMobile={isMobile}
                      isHovered={hoveredProduct === product._id} isFirstProduct={index === 0}
                      onMouseEnter={handleMouseEnter(product._id)} onMouseLeave={handleMouseLeave}
                    />
                  ))
                )}

                {/* Skeleton pagination while loading; real bar once data arrives */}
                {loading
                  ? <PaginationSkeleton />
                  : pages > 1 && <Pagination pages={pages} page={page} keyword={keyword} />
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ShopSection;